/**
 * Helpers do Centro de Mensagens — fonte única de verdade para configuração de
 * canal, normalização de histórico, detecção de contato desconhecido e
 * formatação de timestamps. Mantido fora dos componentes para facilitar reuso e
 * testes.
 */

import {
  getSafePhone,
  getSafeUsername,
  looksLikeBsuid,
  resolveIdentityState,
} from "../../utils/identity";

/**
 * Configuração de canal por `platform`. Cores derivadas da paleta da marca /
 * cores oficiais das plataformas; nunca hardcoded inline nos componentes.
 *
 *  - whatsapp  → verde WhatsApp (#25D366)
 *  - instagram → gradiente Instagram (#E1306C → #833AB4)
 *  - widget    → Nexus Blue (#3498DB)
 */
export const CHANNEL_COLORS = {
  whatsapp: {
    label: "WhatsApp",
    icon: "💬",
    color: "#25D366",
    background: "#25D366",
  },
  instagram: {
    label: "Instagram",
    icon: "📷",
    color: "#E1306C",
    background: "linear-gradient(135deg, #E1306C 0%, #833AB4 100%)",
  },
  widget: {
    label: "Widget",
    icon: "🌐",
    color: "#3498DB",
    background: "#3498DB",
  },
};

const DEFAULT_CHANNEL = {
  label: "Mensagem",
  icon: "💬",
  color: "#7f8c8d",
  background: "#7f8c8d",
};

/** Resolve a config de canal, com fallback neutro para plataformas desconhecidas. */
export function getChannelConfig(platform) {
  return CHANNEL_COLORS[platform] ?? DEFAULT_CHANNEL;
}

// ── Filas (F2.2) ─────────────────────────────────────────────────────────────

/**
 * As três filas da inbox. `INBOX` é o estado-padrão: uma sessão sem
 * `queueStatus` (dado antigo / retro-compat) OU com valor desconhecido é
 * sempre tratada como INBOX.
 */
/**
 * Detecta o 403 de plano que NÃO inclui a capacidade pedida. O `planMiddleware`
 * do backend emite `{ code: "FEATURE_NOT_INCLUDED" }` tanto para CRM (Central de
 * Atendimento, plano Básico) quanto para agendamento (Plano Atendimento,
 * ADR-0026). Serve de defesa em profundidade: a UI já bloqueia essas ações, mas
 * se uma escapar, tratamos com o CTA de upgrade em vez de um erro cru.
 *
 * ⚠️ Reexport: a definição canônica mora em `utils/planErrors.js` desde que o
 * mesmo 403 passou a valer para duas capacidades (invariante 4 — uma função por
 * formato). Mantido aqui só para não quebrar os imports desta pasta.
 */
export { isFeatureNotIncluded } from "../../utils/planErrors";

export const QUEUE_STATUSES = ["INBOX", "WAITING", "DONE"];

/**
 * Fila efetiva de uma sessão, com fallback seguro para "INBOX".
 * @param {object|null|undefined} session
 * @returns {"INBOX"|"WAITING"|"DONE"}
 */
export function getQueueStatus(session) {
  const raw = session?.queueStatus;
  return QUEUE_STATUSES.includes(raw) ? raw : "INBOX";
}

/**
 * Conta as sessões por fila. Sessão sem `queueStatus` conta como INBOX.
 * @param {Array} sessions
 * @returns {{ INBOX: number, WAITING: number, DONE: number }}
 */
export function countByQueue(sessions) {
  const counts = { INBOX: 0, WAITING: 0, DONE: 0 };
  if (!Array.isArray(sessions)) return counts;
  for (const session of sessions) counts[getQueueStatus(session)] += 1;
  return counts;
}

/**
 * Filtra as sessões pela fila alvo. Alvo inválido cai em INBOX (retro-compat).
 * @param {Array} sessions
 * @param {string} queueStatus
 * @returns {Array}
 */
export function filterByQueue(sessions, queueStatus) {
  if (!Array.isArray(sessions)) return [];
  const target = QUEUE_STATUSES.includes(queueStatus) ? queueStatus : "INBOX";
  return sessions.filter((session) => getQueueStatus(session) === target);
}

// ── Atribuição de conversa (F2.4) ────────────────────────────────────────────

/**
 * Resolve um `departmentId` para `{ name, color }` a partir do mapa de setores
 * (`id → department`). Retorna `null` quando não há id, mapa ou setor
 * correspondente. `color` cai para `null` quando o setor não define cor — o
 * componente decide o fallback (ex.: `stringToColor(name)`).
 * @param {string|null|undefined} departmentId
 * @param {Record<string, {name?: string, color?: string}>|null|undefined} departmentsById
 * @returns {{ name: string, color: string|null }|null}
 */
export function resolveDepartment(departmentId, departmentsById) {
  if (!departmentId || !departmentsById) return null;
  const dept = departmentsById[departmentId];
  if (!dept) return null;
  return { name: dept.name, color: dept.color ?? null };
}

/**
 * Valor atual do seletor de atribuição para uma sessão. Prefere o atendente
 * (mais específico) ao setor. Vazio quando não há atribuição.
 *  - com `assignedToId` → `"member:<id>"`
 *  - só com `departmentId` → `"dept:<id>"`
 *  - sem nada → `""`
 * @param {{assignedToId?: string|null, departmentId?: string|null}|null|undefined} session
 * @returns {string}
 */
export function currentAssignmentValue(session) {
  if (session?.assignedToId) return `member:${session.assignedToId}`;
  if (session?.departmentId) return `dept:${session.departmentId}`;
  return "";
}

/**
 * Traduz o valor escolhido no seletor de atribuição para o corpo do
 * `PATCH /tenants/sessions/:id/assign`.
 *  - `"unassign"`      → `{ assignedToId: null }` (desatribui)
 *  - `"dept:<id>"`     → `{ departmentId: <id> }` (round-robin no setor)
 *  - `"member:<id>"`   → `{ assignedToId: <id> }` (atribuição direta)
 *  - vazio/desconhecido→ `null` (nenhuma ação)
 * @param {string} value
 * @returns {{assignedToId: string|null}|{departmentId: string}|null}
 */
export function assignmentPayloadFromValue(value) {
  if (!value) return null;
  if (value === "unassign") return { assignedToId: null };
  if (value.startsWith("dept:")) return { departmentId: value.slice(5) };
  if (value.startsWith("member:")) return { assignedToId: value.slice(7) };
  return null;
}

/** Rótulo de exibição de um membro do tenant (nome → e-mail → genérico). */
export function memberDisplayName(member) {
  return member?.user?.name || member?.user?.email || "Atendente";
}

// ── Tags livres por conversa (F2.5) ──────────────────────────────────────────

/**
 * Tags de uma sessão, sempre como array. Sessão antiga sem `tags` (retro-compat)
 * ou valor não-array degrada para `[]`.
 * @param {{tags?: Array}|null|undefined} session
 * @returns {string[]}
 */
export function getSessionTags(session) {
  const raw = session?.tags;
  return Array.isArray(raw) ? raw : [];
}

/**
 * Normaliza um array de tags espelhando o backend (F2.5): faz trim, descarta
 * vazias e deduplica preservando a primeira ocorrência (case-sensitive —
 * "VIP" e "vip" coexistem). Mantém a ordem de inserção. Usado no cliente para
 * refletir a mesma tag que o servidor devolverá, sem esperar a resposta.
 * @param {Array|null|undefined} tags
 * @returns {string[]}
 */
export function normalizeTags(tags) {
  if (!Array.isArray(tags)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of tags) {
    const tag = String(raw ?? "").trim();
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
  }
  return out;
}

/**
 * Retorna um novo array com `raw` adicionada e normalizada. No-op semântico para
 * entrada vazia/só-espaços ou tag já presente (o resultado normalizado terá o
 * mesmo comprimento do original — o chamador pode comparar para evitar PATCH).
 * @param {string[]} tags
 * @param {string} raw
 * @returns {string[]}
 */
export function addTag(tags, raw) {
  return normalizeTags([...(Array.isArray(tags) ? tags : []), raw]);
}

/**
 * Remove uma tag do array (comparação exata, case-sensitive).
 * @param {string[]} tags
 * @param {string} tag
 * @returns {string[]}
 */
export function removeTag(tags, tag) {
  if (!Array.isArray(tags)) return [];
  return tags.filter((t) => t !== tag);
}

/**
 * União ordenada (pt-BR) de todas as tags presentes nas sessões carregadas —
 * as opções do filtro por tag. Deduplica case-sensitive.
 * @param {Array} sessions
 * @returns {string[]}
 */
export function uniqueTags(sessions) {
  if (!Array.isArray(sessions)) return [];
  const set = new Set();
  for (const s of sessions) for (const t of getSessionTags(s)) set.add(t);
  return Array.from(set).sort((a, b) => a.localeCompare(b, "pt-BR"));
}

/**
 * Filtra as sessões que contêm a tag alvo (client-side, consistente com as
 * abas de fila e o chip de canal). Tag vazia/nula → não filtra (todas passam).
 * @param {Array} sessions
 * @param {string|null|undefined} tag
 * @returns {Array}
 */
export function filterByTag(sessions, tag) {
  if (!Array.isArray(sessions)) return [];
  if (!tag) return sessions;
  return sessions.filter((s) => getSessionTags(s).includes(tag));
}

// ── Notas internas por conversa (F2.6) ───────────────────────────────────────

/**
 * Rótulo de exibição do autor de uma nota interna. O nome é um snapshot gravado
 * no momento em que a nota foi criada, então ele sobrevive mesmo depois de o
 * autor sair da equipe. Quando `authorId === null` (autor removido do tenant), o
 * nome ainda é mostrado, com o sufixo " (removido)" para deixar claro que aquela
 * pessoa não faz mais parte da equipe.
 * @param {{authorId?: string|null, authorName?: string|null}|null|undefined} note
 * @returns {string}
 */
export function formatNoteAuthor(note) {
  const name = String(note?.authorName ?? "").trim() || "Atendente";
  // Sufixo estrito em `authorId === null` (contrato F2.6: autor removido). Um
  // `authorId` ausente/undefined (entrada defensiva) NÃO recebe o sufixo.
  return note?.authorId === null ? `${name} (removido)` : name;
}

/**
 * Decide se o usuário atual pode excluir uma nota interna (F2.6). OWNER e ADMIN
 * podem excluir qualquer nota; um MEMBER só pode excluir a própria nota
 * (`note.authorId === memberId`). Uma nota de autor removido (`authorId === null`)
 * nunca pertence a um membro, então só cai para OWNER/ADMIN.
 * @param {{authorId?: string|null}|null|undefined} note
 * @param {{role?: string|null, memberId?: string|null}} [viewer]
 * @returns {boolean}
 */
export function canDeleteNote(note, { role, memberId } = {}) {
  if (role === "OWNER" || role === "ADMIN") return true;
  if (!memberId || note?.authorId == null) return false;
  return note.authorId === memberId;
}

/**
 * Normaliza o `history` de uma sessão antes de qualquer `.map()`.
 * O backend pode entregar um array plano OU um objeto `{ messages: [] }`
 * (bug histórico documentado). Sempre retorna um array.
 * @param {Array|{messages?: Array}|null|undefined} history
 * @returns {Array}
 */
export function getHistoryMessages(history) {
  if (!history) return [];
  if (Array.isArray(history)) return history;
  if (history.messages && Array.isArray(history.messages)) return history.messages;
  return [];
}

/**
 * Um nome é "desconhecido" quando ausente, vazio ou um placeholder gerado
 * ("ID: ...", "ID:...user"). Nesses casos a UI mostra "Contato desconhecido".
 * @param {string|null|undefined} clientName
 * @returns {boolean}
 */
export function isUnknownContact(clientName) {
  if (!clientName) return true;
  const trimmed = String(clientName).trim();
  if (!trimmed) return true;
  return /^id:/i.test(trimmed) || looksLikeBsuid(trimmed);
}

/** Rótulo de exibição do contato. */
export function getDisplayName(clientName) {
  return isUnknownContact(clientName)
    ? "Contato desconhecido"
    : String(clientName).trim();
}

/**
 * Deriva a identidade de exibição de uma sessão de conversa (ADR-0016 / Fase 5).
 * NUNCA expõe BSUID cru — apenas estado + username + telefone (quando visível).
 * Degrada com segurança quando o backend não envia `identityState` (dev antigo).
 *
 * @param {object} session
 * @returns {{ state: "phone"|"hidden"|"username", username: string|null,
 *             phoneNumber: string|null, hasHiddenPhone: boolean,
 *             isPhoneVisible: boolean }}
 */
export function getSessionIdentity(session = {}) {
  const safePhone = getSafePhone(session.phoneNumber);
  const safeUsername = getSafeUsername(session.username);
  const state = resolveIdentityState({
    identityState: session.identityState,
    hasHiddenPhone: session.hasHiddenPhone,
    phone: safePhone,
    username: safeUsername,
  });
  const isPhoneVisible = state === "phone" && Boolean(safePhone);
  return {
    state,
    username: safeUsername || null,
    phoneNumber: isPhoneVisible ? safePhone : null,
    hasHiddenPhone: state !== "phone",
    isPhoneVisible,
  };
}

/**
 * Iniciais (até 2) a partir do nome. Apenas para contatos conhecidos.
 * @param {string} clientName
 * @returns {string}
 */
export function getInitials(clientName) {
  if (isUnknownContact(clientName)) return "";
  const parts = String(clientName).trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

/**
 * Mapeia o `role` da mensagem para a semântica de bolha do Centro de Mensagens.
 *
 * **Dois nomes apontam para a IA.** O grafo vivo grava `role: "bot"`
 * (`backend/src/graph/invokeGraph.ts`), enquanto histórico antigo usa `"model"`
 * (nomenclatura do Gemini). Os dois precisam virar `"ai"`: sem isso, `"bot"`
 * cai no fallback e a resposta da IA renderiza como bolha de atendente.
 *
 * Qualquer outro role (`"human"` — seja `human_app` pelo WhatsApp Business ou
 * `human_panel` pelo painel — e desconhecidos) é tratado como atendente humano.
 *
 * @param {string|null|undefined} role
 * @returns {"client"|"ai"|"human"}
 */
export function classifyRole(role) {
  if (role === "user") return "client"; // cliente → bolha à esquerda
  if (role === "bot" || role === "model") return "ai"; // IA → bolha à direita
  return "human"; // atendente / desconhecido → bolha à direita
}

/**
 * Ícone + rótulo de placeholder por `mediaType`.
 *
 * Mensagens da coexistência WhatsApp chegam com `mediaType` e `text` vazio ou
 * um placeholder gerado (`"[Imagem]"`, `"[audio]"`…). A `mediaUrl` é um
 * `asset_id` da Meta, **não** uma URL pública — por isso a UI nunca tenta
 * renderizar a mídia, só descreve o que chegou.
 */
const MEDIA_PLACEHOLDERS = {
  image: { icon: "📷", short: "Foto", full: "Foto recebida" },
  audio: { icon: "🎧", short: "Áudio", full: "Áudio recebido" },
  document: { icon: "📄", short: "Documento", full: "Documento recebido" },
};

/** Fallback para `mediaType` desconhecido — evita bolha/preview vazios. */
const GENERIC_MEDIA = { icon: "📎", short: "Arquivo", full: "Arquivo recebido" };

/**
 * Placeholder da mídia, ou `null` quando a mensagem não tem mídia.
 * @param {string|null|undefined} mediaType
 * @returns {{icon: string, short: string, full: string}|null}
 */
export function getMediaPlaceholder(mediaType) {
  if (!mediaType) return null;
  return MEDIA_PLACEHOLDERS[String(mediaType).toLowerCase()] ?? GENERIC_MEDIA;
}

/**
 * `true` quando o texto é só o placeholder gerado pelo backend — `"[Imagem]"`
 * (webhook) ou `"[image]"`/`"[audio]"` (coexistência) — e não uma legenda real.
 * @param {string|null|undefined} text
 * @returns {boolean}
 */
export function isMediaPlaceholderText(text) {
  if (!text) return true;
  return /^\[[^\]]*\]$/.test(String(text).trim());
}

/** Texto de preview da última mensagem (trata mídia sem texto). */
export function previewMessage(msg) {
  if (!msg) return "";
  const media = getMediaPlaceholder(msg.mediaType);
  if (!media) return msg.text || "";
  return isMediaPlaceholderText(msg.text)
    ? `${media.icon} ${media.short}`
    : `${media.icon} ${msg.text}`;
}

// ── Timestamps ───────────────────────────────────────────────────────────────

const MIN = 60 * 1000;
const HOUR = 60 * MIN;
const DAY = 24 * HOUR;

/**
 * Timestamp relativo compacto para a lista: "agora", "há 5min", "há 3h",
 * "ontem", "15/05". Tolerante a entradas inválidas (retorna "").
 * @param {string|number|Date|null|undefined} value
 * @returns {string}
 */
export function compactRelativeTime(value) {
  if (!value) return "";
  const date = value instanceof Date ? value : new Date(value);
  const ts = date.getTime();
  if (Number.isNaN(ts)) return "";

  const diff = Date.now() - ts;
  if (diff < MIN) return "agora";
  if (diff < HOUR) return `há ${Math.floor(diff / MIN)}min`;
  if (diff < DAY) return `há ${Math.floor(diff / HOUR)}h`;
  if (diff < 2 * DAY) return "ontem";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
  }).format(date);
}
